import React, { useState, useEffect } from 'react';

const API_BASE = process.env.NODE_ENV === 'production'
  ? 'https://gammalens-api.onrender.com'
  : 'http://localhost:8000';

const STORAGE_KEY = 'gl_feedback_dismissed_v1';
const SUBMITTED_KEY = 'gl_feedback_submitted_v1';

export function FeedbackBanner({ user, onOpen }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!user) return;
    const dismissed = localStorage.getItem(STORAGE_KEY);
    const submitted = localStorage.getItem(SUBMITTED_KEY);
    if (!dismissed && !submitted) setVisible(true);
  }, [user]);

  const dismiss = (e) => {
    e.stopPropagation();
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      onClick={onOpen}
      style={{
        marginBottom: '12px',
        padding: '8px 14px',
        borderRadius: '8px',
        background: 'rgba(48,209,88,0.06)',
        border: '1px solid rgba(48,209,88,0.18)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: '10px', cursor: 'pointer',
        animation: 'gl-fb-slide 0.4s ease both',
      }}
    >
      <style>{`
        @keyframes gl-fb-slide { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: none; } }
      `}</style>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#30d158" strokeWidth="2.2" style={{ flexShrink: 0 }}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
        <span style={{ fontSize: '12px', fontWeight: 600, color: '#aeaeb2' }}>Share feedback</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
        <span style={{ fontSize: '12px', color: '#30d158', fontWeight: 600 }}>→</span>
        <button onClick={dismiss} style={{
          background: 'transparent', border: 'none', color: '#636366',
          cursor: 'pointer', padding: '2px 6px', fontSize: '14px', lineHeight: 1,
        }} title="Dismiss">×</button>
      </div>
    </div>
  );
}

export default function FeedbackModal({ open, onClose, user }) {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setRating(0); setMessage(''); setDone(false); setError('');
    }
  }, [open]);

  if (!open) return null;

  const submit = async () => {
    setError('');
    if (rating < 1) { setError('Pick a star rating first'); return; }
    if (message.trim().length < 10) { setError('Tell us a bit more (10 chars minimum)'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rating,
          message: message.trim(),
          name: user?.display_name || null,
          email: user?.email || null,
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Submission failed');
      }
      localStorage.setItem(SUBMITTED_KEY, Date.now().toString());
      setDone(true);
      setTimeout(() => onClose(), 2000);
    } catch (e) {
      setError(e.message || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px', animation: 'gl-fb-fade 0.2s ease',
      }}
    >
      <style>{`
        @keyframes gl-fb-fade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes gl-fb-pop { from { opacity: 0; transform: scale(0.96) translateY(8px); } to { opacity: 1; transform: none; } }
      `}</style>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: '480px',
          background: 'linear-gradient(160deg, rgba(26,26,31,0.98), rgba(15,15,18,0.98))',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '16px', padding: '28px',
          backdropFilter: 'blur(40px)',
          boxShadow: '0 30px 80px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.05)',
          animation: 'gl-fb-pop 0.3s cubic-bezier(0.16,1,0.3,1)',
        }}
      >
        {done ? (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <div style={{
              width: '48px', height: '48px', borderRadius: '50%',
              background: 'rgba(48,209,88,0.18)', margin: '0 auto 16px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#30d158" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
            <div style={{ fontSize: '17px', fontWeight: 700, color: '#fff', marginBottom: '6px' }}>Thanks for your feedback!</div>
            <div style={{ fontSize: '13px', color: '#8e8e93' }}>Every word helps shape GammaLens.</div>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '18px' }}>
              <div>
                <div style={{ fontSize: '17px', fontWeight: 800, color: '#fff', letterSpacing: '-0.3px' }}>Share feedback</div>
                <div style={{ fontSize: '12px', color: '#8e8e93', marginTop: '3px' }}>Honest, blunt, anything — it goes straight to the founder</div>
              </div>
              <button onClick={onClose} style={{
                background: 'transparent', border: 'none', color: '#8e8e93',
                fontSize: '22px', cursor: 'pointer', padding: '0 4px', lineHeight: 1,
              }}>×</button>
            </div>

            {/* Star rating */}
            <div style={{ marginBottom: '18px' }}>
              <div style={{ fontSize: '11px', color: '#8e8e93', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>Rating</div>
              <div style={{ display: 'flex', gap: '6px' }}>
                {[1, 2, 3, 4, 5].map(n => (
                  <button
                    key={n}
                    onClick={() => setRating(n)}
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                    style={{
                      background: 'transparent', border: 'none', cursor: 'pointer',
                      fontSize: '28px', padding: '2px 4px', lineHeight: 1,
                      color: (hover || rating) >= n ? '#ff9f0a' : 'rgba(255,255,255,0.15)',
                      transition: 'color 0.15s, transform 0.15s',
                      transform: hover === n ? 'scale(1.15)' : 'scale(1)',
                    }}
                  >★</button>
                ))}
              </div>
            </div>

            {/* Message */}
            <div style={{ marginBottom: '14px' }}>
              <div style={{ fontSize: '11px', color: '#8e8e93', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>What's on your mind?</div>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder="What's working? What's broken? What would make this 10x better?"
                rows={5}
                maxLength={2000}
                style={{
                  width: '100%', padding: '12px 14px', borderRadius: '10px',
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  color: '#f2f2f7', fontSize: '16px', fontFamily: 'inherit',
                  resize: 'vertical', outline: 'none', lineHeight: 1.5,
                  boxSizing: 'border-box',
                }}
              />
              <div style={{ fontSize: '10px', color: '#636366', marginTop: '4px', textAlign: 'right' }}>
                {message.length} / 2000
              </div>
            </div>

            {error && (
              <div style={{ fontSize: '12px', color: '#ff453a', marginBottom: '12px', padding: '8px 12px', background: 'rgba(255,69,58,0.08)', borderRadius: '6px' }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <button
                onClick={submit}
                disabled={submitting}
                style={{
                  flex: 1, padding: '11px 20px', borderRadius: '10px',
                  background: submitting ? 'rgba(48,209,88,0.4)' : '#30d158',
                  border: 'none', color: '#000', fontSize: '13px', fontWeight: 700,
                  cursor: submitting ? 'wait' : 'pointer', fontFamily: 'inherit',
                  letterSpacing: '0.02em',
                }}
              >{submitting ? 'Sending...' : 'Send feedback'}</button>
              <button
                onClick={onClose}
                style={{
                  padding: '11px 18px', borderRadius: '10px',
                  background: 'transparent', border: '1px solid rgba(255,255,255,0.1)',
                  color: '#8e8e93', fontSize: '12px', fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'inherit',
                }}
              >Cancel</button>
            </div>

            {user && (
              <div style={{ fontSize: '10px', color: '#636366', marginTop: '12px', textAlign: 'center' }}>
                Sent as {user.display_name || user.email}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
